import React, { Suspense, lazy, useEffect } from 'react';
import checkAdminAuth from './helpers/AdminAuth.js';
import LoginForm from './components/loginauth/Login';
import Home from './components/Home.js';
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import Layout from "scenes/layout";
import Dashboard from "scenes/dashboard";

import PageLoader from "components/PageLoader";

// Preserve the existing global stylesheet order when page JavaScript is split.
import "react-datepicker/dist/react-datepicker.css";
import "scenes/sessions/style.css";
import "./style.css";
import "react-phone-input-2/lib/material.css";
import "scenes/team/Team.css";
import "react-phone-input-2/lib/style.css";
import "components/styles.css";
import "react-phone-number-input/style.css";
import "react-image-crop/dist/ReactCrop.css";
import "normalize.css/normalize.css";

// Keep the dashboard ready immediately; fetch other page code only when visited.
const Sessions = lazy(() => import("scenes/sessions"));
const Customers = lazy(() => import("scenes/customers"));
const ParsedLogs = lazy(() => import("scenes/parsedlogs"));
const Geography = lazy(() => import("scenes/geography"));
const Yearly = lazy(() => import("scenes/overview"));
const Daily = lazy(() => import("scenes/daily"));
const Monthly = lazy(() => import("scenes/monthly"));
const Breakdown = lazy(() => import("scenes/breakdown"));
const Admin = lazy(() => import("scenes/admin"));
const Performance = lazy(() => import("scenes/performance"));
const Team = lazy(() => import("scenes/team"));
const CreateUser = lazy(() => import("scenes/adduser"));
const ForgotPassword = lazy(() => import("components/ForgotPassword.jsx"));
const PasswordReset = lazy(() => import("components/PasswordReset.jsx"));
const UnAuthorized = lazy(() => import("components/UnAuthorized.jsx"));
const Page404 = lazy(() => import("components/page404.js"));
const Privacypolicy = lazy(() => import("components/privacypolicy.jsx"));
const Footer = lazy(() => import("components/Footer.js"));
const UpdateProfile = lazy(() => import("components/UpdateProfile.jsx"));
const ActivateAccount = lazy(() => import("scenes/adduser/ActivateAccount.jsx"));
const ParsedCustomerData = lazy(() => import("scenes/parsedlogs/customerData.jsx"));
const Reports = lazy(() => import("scenes/reports"));
const ContactDirectory = lazy(() => import("scenes/customers/Contacts/ContactDirectory"));
const Invoices = lazy(() => import("scenes/sessions/invoices.jsx"));
const Contacts = lazy(() => import("scenes/customers/Contacts"));
const PaymentHistory = lazy(() => import("scenes/historpage/index.jsx"));
const CurrentPlan = lazy(() => import("scenes/currenrplan/index.jsx"));
const ManageSubscription = lazy(() => import("scenes/managesubscription/index.jsx"));
const Meetings = lazy(() => import("scenes/meetings"));
const BillingSuccess = lazy(() => import("scenes/billing/BillingSuccess"));
const CheckoutPage = lazy(() => import("scenes/billing/CheckoutPage"));
const SqlDataMigration = lazy(() => import("scenes/admin/SqlDataMigration"));

const PAGE_TITLES = {
  "/":                              "Home",
  "/user/login":                    "Login",
  "/user/forgotpassword":           "Forgot Password",
  "/user/passwordreset":            "Reset Password",
  "/activate-account":              "Activate Account",
  "/user/login/unauthorized":       "Unauthorized",
  "/billing/success":               "Payment Successful",
  "/billing/cancel":                "Billing Cancelled",
  "/privacypolicy":                 "Privacy Policy",
  "/footer":                        "Footer",
  // Protected
  "/dashboard":                     "Dashboard",
  "/reports":                       "Reports",
  "/parsedlogs":                    "Parsed Logs",
  "/parsedcustomerdata":            "Customer Data",
  "/yearly":                        "Yearly Overview",
  "/daily":                         "Daily Overview",
  "/monthly":                       "Monthly Overview",
  "/sessions":                      "Sessions",
  "/invoices":                      "Invoices",
  "/customers/breakdown":           "Customers Breakdown",
  "/invoices/breakdown":            "Invoices Breakdown",
  "/geography":                     "Geography",
  "/invoices/geography":            "Invoices Geography",
  "/customers/geography":           "Customers Geography",
  "/admin":                         "Admin",
  "/admin/sql-data-migration":     "Load Data from SQL Server",
  "/team":                          "Team",
  "/createuser":                    "Create User",
  "/customers":                     "Customers",
  "/customers/contacts":            "Customer Contacts",
  "/performance":                   "Performance",
  "/updateprofile":                 "Update Profile",
  "/billing/current-plan":          "Current Plan",
  "/billing/manage-subscription":   "Manage Subscription",
  "/billing/payment-history":       "Payment History",
  "/billing/checkout":              "Checkout",
  "/meetings":                      "Meetings",
};

const APP_NAME = "OneFinTrace";

function useDynamicTitle() {
  const location = useLocation();

  useEffect(() => {
    const path = location.pathname.toLowerCase();

    let label = PAGE_TITLES[path];

  
    if (!label) {
      const prefix = Object.keys(PAGE_TITLES).find(
        (key) => path.startsWith(key + "/") || path.startsWith(key)
      );
      if (prefix) label = PAGE_TITLES[prefix];
    }

    document.title = label ? `${label} | ${APP_NAME}` : APP_NAME;
  }, [location.pathname]);
}
function TitleManager() {
  useDynamicTitle();
  return null;
}

const PrivateRoute = ({ children, roles = [] }) => {
  const { authed, role } = checkAdminAuth();

  if (!authed || !role) {
    return <Navigate to="/user/login" replace />;
  }

  if (!roles.includes(role)) {
    return <Navigate to="/user/login/unAuthorized" replace />;
  }

  return children;
};

const BaseRoute = () => (
  <>
   
    <TitleManager />

    <Suspense fallback={<PageLoader variant="viewport" />}>
      <Routes>
        {/* ── Public routes ──────────────────────────────────────────────────── */}
        <Route exact path="/"                        element={<Home />} />
        <Route exact path="/user/login"              element={<LoginForm />} />
        <Route exact path="/user/ForgotPassword"     element={<ForgotPassword />} />
        <Route exact path="/user/PasswordReset"      element={<PasswordReset />} />
        <Route        path="/activate-account"       element={<ActivateAccount />} />
        <Route exact path="/user/login/unAuthorized" element={<UnAuthorized />} />
        <Route        path="/billing/success"        element={<BillingSuccess />} />
        <Route        path="/billing/cancel"         element={<Navigate to="/billing/manage-subscription" />} />
        <Route exact path="/privacypolicy"           element={<Privacypolicy />} />
        <Route exact path="/Footer"                  element={<Footer />} />
        <Route exact path="*"                        element={<Page404 />} />

        {/* ── Protected routes — SubscriptionBanner inside Layout handles warning */}
        <Route
          element={
            <PrivateRoute roles={["admin", "superadmin", "user"]}>
              <Layout />
            </PrivateRoute>
          }
        >
          {/* ── Admin + User routes ─────────────────────────────────────────── */}
          <Route path="/dashboard"          element={<PrivateRoute roles={["admin", "user"]}><Dashboard /></PrivateRoute>} />
          <Route path="/parsedlogs"         element={<PrivateRoute roles={["admin", "user"]}><ParsedLogs /></PrivateRoute>} />
          <Route path="/parsedcustomerdata" element={<PrivateRoute roles={["admin", "user"]}><ParsedCustomerData /></PrivateRoute>} />
          <Route path="/Yearly"             element={<PrivateRoute roles={["admin", "user"]}><Yearly /></PrivateRoute>} />
          <Route path="/daily"              element={<PrivateRoute roles={["admin", "user"]}><Daily /></PrivateRoute>} />
          <Route path="/monthly"            element={<PrivateRoute roles={["admin", "user"]}><Monthly /></PrivateRoute>} />
          <Route path="/sessions"           element={<PrivateRoute roles={["admin", "user"]}><Sessions /></PrivateRoute>} />
          <Route path="/reports" element={<PrivateRoute roles={["admin", "user"]}><Reports /></PrivateRoute>} />
          <Route path="/invoices"           element={<PrivateRoute roles={["admin", "user"]}><Invoices /></PrivateRoute>} />
          <Route path="/customers/breakdown" element={<PrivateRoute roles={["admin", "user"]}><Breakdown view="customers" /></PrivateRoute>} />
          <Route path="/invoices/breakdown"  element={<PrivateRoute roles={["admin", "user"]}><Breakdown view="invoices" /></PrivateRoute>} />
          <Route path="/geography"           element={<PrivateRoute roles={["admin", "user"]}><Geography /></PrivateRoute>} />
          <Route path="/invoices/geography"  element={<PrivateRoute roles={["admin", "user"]}><Geography view="invoices" /></PrivateRoute>} />
          <Route path="/customers/geography" element={<PrivateRoute roles={["admin", "user"]}><Geography view="customers" /></PrivateRoute>} />

          {/* ── Superadmin + Admin routes ───────────────────────────────────── */}
          <Route path="/admin"       element={<PrivateRoute roles={["superadmin"]}><Admin /></PrivateRoute>} />
          <Route path="/admin/sql-data-migration" element={<PrivateRoute roles={["superadmin"]}><SqlDataMigration /></PrivateRoute>} />
          <Route path="/team"        element={<PrivateRoute roles={["superadmin", "admin"]}><Team /></PrivateRoute>} />
          <Route path="/createuser"  element={<PrivateRoute roles={["superadmin", "admin"]}><CreateUser /></PrivateRoute>} />
          <Route path="/customers"   element={<PrivateRoute roles={["superadmin", "admin"]}><Customers /></PrivateRoute>} />
          <Route path="/customers/contacts/all" element={<PrivateRoute roles={["superadmin", "admin"]}><ContactDirectory /></PrivateRoute>} />
          <Route path="/customers/contacts/:customerId" element={<PrivateRoute roles={["superadmin", "admin"]}><Contacts /></PrivateRoute>} />
          <Route path="/performance" element={<PrivateRoute roles={["superadmin", "admin"]}><Performance /></PrivateRoute>} />

          {/* ── All roles ───────────────────────────────────────────────────── */}
          <Route path="/Updateprofile" element={<PrivateRoute roles={["superadmin", "admin", "user"]}><UpdateProfile /></PrivateRoute>} />

          {/* ── Billing — admin only ────────────────────────────────────────── */}
          <Route path="/billing/current-plan"        element={<PrivateRoute roles={["admin"]}><CurrentPlan /></PrivateRoute>} />
          <Route path="/billing/manage-subscription" element={<PrivateRoute roles={["admin"]}><ManageSubscription /></PrivateRoute>} />
          <Route path="/billing/payment-history"     element={<PrivateRoute roles={["admin"]}><PaymentHistory /></PrivateRoute>} />
          <Route path="/billing/checkout"            element={<PrivateRoute roles={["admin"]}><CheckoutPage /></PrivateRoute>} />

          <Route path="/meetings" element={<Meetings />} />
        </Route>
      </Routes>
    </Suspense>
  </>
);

export default BaseRoute;
